import { isEnvelope, type EnvelopeMetadata } from './envelope'

export const AUTH_TOKEN_KEY = 'auth_token'
export const AUTH_USER_KEY = 'auth_user'

export const API_BASE_URL =
  import.meta.env.VITE_API_URL

function decodeBase64(value: string): string {
  if (typeof window === 'undefined') {
    return Buffer.from(value, 'base64').toString('binary')
  }
  return atob(value)
}

export function decodeJwtPayload(
  token: string
): Record<string, unknown> | null {
  try {
    const [, payload] = token.split('.')
    if (!payload) {
      return null
    }

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padding = (4 - (normalized.length % 4)) % 4
    const padded = normalized + '='.repeat(padding)
    const decoded = decodeBase64(padded)

    return JSON.parse(decoded)
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Erro ao decodificar payload do token JWT', error)
    }
    return null
  }
}

export interface ResponseMetadata {
  headers: Record<string, string>
  status: number
}

export interface ApiSuccessResponse<T> {
  success: true
  data: T
  metadata: ResponseMetadata
  /**
   * Quando o backend retorna no formato envelope (`{data, metadata, warnings}`),
   * `data` é automaticamente desempacotado e o resto fica acessível aqui.
   * Indefinido pra endpoints legados que não usam envelope.
   */
  envelope?: {
    metadata: EnvelopeMetadata
    warnings: string[]
  }
}

export interface ApiErrorResponse {
  success: false
  error: string
  status?: number
}

export type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse

interface RequestOptions extends RequestInit {
  useAuth?: boolean
  params?: Record<string, unknown>
}

async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> {
  try {
    const { useAuth = false, params, ...customOptions } = options

    let url = endpoint
    if (params) {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })
      const query = searchParams.toString()
      if (query) url += `?${query}`
    }

    const headers = new Headers(customOptions.headers || {})
    if (!(options.body instanceof FormData) && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }

    if (useAuth) {
      const token = localStorage.getItem(AUTH_TOKEN_KEY)
      if (!token) {
        return {
          success: false,
          error: 'Token de autenticação não encontrado.',
          status: 401,
        }
      }

      // Verifica se o token está expirado antes de fazer a requisição
      const payload = decodeJwtPayload(token)
      const expSeconds = Number(
        (payload as { exp?: number | string } | null)?.exp ?? NaN
      )
      if (Number.isFinite(expSeconds) && Date.now() >= expSeconds * 1000) {
        // Limpa o token expirado
        localStorage.removeItem(AUTH_TOKEN_KEY)
        localStorage.removeItem('auth_user')
        return {
          success: false,
          error: 'Sessão expirada.',
          status: 401,
        }
      }
      // Remove espaços e quebras de linha do token
      const cleanToken = token.trim().replace(/\s/g, '')

      // Verifica formato do token
      if (cleanToken.split('.').length !== 3) {
        return {
          success: false,
          error: 'Token com formato inválido. Faça login novamente.',
          status: 401,
        }
      }

      headers.set('Authorization', `Bearer ${cleanToken}`)
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...customOptions,
      headers,
    })

    const responseHeaders: Record<string, string> = {}
    response.headers.forEach((value, key) => {
      responseHeaders[key.toLowerCase()] = value
    })

    // Verifica se a resposta é JSON
    const contentType = response.headers.get('content-type')
    let data
    if (contentType && contentType.includes('application/json')) {
      try {
        data = await response.json()
      } catch (jsonError) {
        const text = await response.text()
        data = {
          error: text || `Erro ao processar resposta (${response.status})`,
        }
      }
    } else {
      const text = await response.text()
      try {
        data = JSON.parse(text)
      } catch {
        data = { error: text || `Erro inesperado (${response.status})` }
      }
    }

    if (!response.ok) {
      // Tenta extrair mensagem de erro de diferentes formatos
      let errorMessage = `Erro inesperado (${response.status})`

      if (data) {
        if (typeof data === 'string') {
          errorMessage = data
        } else if (data.error) {
          errorMessage = data.error
        } else if (data.message) {
          errorMessage = data.message
        } else if (Array.isArray(data) && data.length > 0) {
          errorMessage = data[0]
        }
      }

      return {
        success: false,
        error: errorMessage,
        status: response.status,
      }
    }

    // Auto-unwrap envelope do backend Pulso ({data, metadata, warnings}).
    // Mantém os callsites compatíveis sem precisar tocar em cada um.
    let unwrapped: T = data
    let envelope: ApiSuccessResponse<T>['envelope']
    if (isEnvelope(data)) {
      unwrapped = data.data as T
      envelope = {
        metadata: data.metadata,
        warnings: data.warnings || [],
      }
    }

    return {
      success: true,
      data: unwrapped,
      metadata: {
        headers: responseHeaders,
        status: response.status,
      },
      ...(envelope ? { envelope } : {}),
    }
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return {
        success: false,
        error: `Não foi possível conectar ao servidor (${API_BASE_URL}). Verifique se o backend está rodando e tente novamente.`,
      }
    }

    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Erro inesperado ao comunicar com o servidor. Tente novamente.',
    }
  }
}

export const httpClient = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { method: 'GET', ...options }),
  post: <T>(
    endpoint: string,
    data: FormData | Record<string, unknown>,
    options?: RequestOptions
  ) =>
    request<T>(endpoint, {
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
      ...options,
    }),
  put: <T>(
    endpoint: string,
    data: FormData | Record<string, unknown> | string[],
    options?: RequestOptions
  ) =>
    request<T>(endpoint, {
      method: 'PUT',
      body: data instanceof FormData ? data : JSON.stringify(data),
      ...options,
    }),
  patch: <T>(
    endpoint: string,
    data: FormData | Record<string, unknown>,
    options?: RequestOptions
  ) =>
    request<T>(endpoint, {
      method: 'PATCH',
      body: data instanceof FormData ? data : JSON.stringify(data),
      ...options,
    }),
  delete: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { method: 'DELETE', ...options }),
}

export default httpClient
